import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

type FeatureItem = {
  title: string;
  description: string;
};

const projectSectionDiagram1 = '/images/aip/Project/full-process-standardized-control.png';
const projectSectionDiagram2 = '/images/aip/Project/biz-finance-integration.png';
const projectSectionDiagram3 = '/images/aip/Project/cross-department-workbench.png';
const projectSectionDiagram4 = '/images/aip/Project/intelligent-monitoring-warning.png';
const projectSectionDiagram5 = '/images/aip/Project/intelligent-data-analysis.png';
const projectSectionDiagram6 = '/images/aip/Project/differentiated-value.png';
const case01 = '/images/aip/Retail/case-01.png';
const bannerBg = '/images/aip/Project/project-banner-bg.svg';

type FeatureSectionData = {
  title: string;
  subtitle: string;
  previewAlt: string;
  previewImage: string;
  items: FeatureItem[];
};

const featureSections: FeatureSectionData[] = [
  {
    title: '全流程标准化管控',
    subtitle: '项目执行的“规范导航系统”',
    previewAlt: '全流程标准化管控示意图',
    previewImage: projectSectionDiagram1,
    items: [
      { title: '全周期流程固化', description: '支持从立项到资产转固的全流程标准化配置，所有环节可追溯、可管控。' },
      { title: '统一管控标准', description: '固化立项、预算管控、成本归集等核心标准，形成完整管理闭环。' },
      { title: '标准化项目结构', description: '构建“WBS-作业/里程碑-成本”三层联动模型，实现结构统一管控。' },
      { title: '全链条关联执行', description: '通过多层级WBS分解将项目拆解为可管理单元，保障全链条协同。' }
    ]
  },
  {
    title: '业财一体化融合',
    subtitle: '成本管控的“精准核算中枢”',
    previewAlt: '业财一体化融合示意图',
    previewImage: projectSectionDiagram2,
    items: [
      { title: '预算-实际联动', description: '深度集成财务模块，建立预算-实际-预测全流程管控并实时对比关键值。' },
      { title: '自动成本归集', description: '工时、采购、费用等成本按项目与WBS自动归集并自动过账至总账。' },
      { title: '全链条预算控制', description: '构建“预算→承诺→实际成本”控制机制，提前预警预算超支风险。' },
      { title: '合规化收入确认', description: '集成成本计划与收入计划，支持POC收入确认与自动损益核算。' }
    ]
  },
  {
    title: '跨部门协同工作台',
    subtitle: '资源配置的“高效调度中心”',
    previewAlt: '跨部门协同工作台示意图',
    previewImage: projectSectionDiagram3,
    items: [
      { title: '统一资源管理池', description: '集中管控人力、设备、物料等资源，进行负荷分析与冲突预警。' },
      { title: '业务流程打通', description: '打通项目计划与采购、生产、领料、薪资核算等流程，实现跨部门协同。' },
      { title: '需求计划自动化', description: '与生产、采购、仓库集成，自动预算检查与流转并生成执行单据。' },
      { title: '进度-资源联动', description: '通过甘特图与CPM实现进度可视化，关联资源消耗与成本偏差分析。' }
    ]
  },
  {
    title: '智能监控与预警',
    subtitle: '项目风险的“主动防御体系”',
    previewAlt: '智能监控与预警示意图',
    previewImage: projectSectionDiagram4,
    items: [
      { title: '可视化健康监控', description: '借助甘特图、仪表盘、挣值分析直观呈现进度、成本、盈利等指标。' },
      { title: '智能阈值预警', description: '预设进度与成本阈值，异常自动告警并推送责任人。' },
      { title: '风险闭环管理', description: '建立风险登记、评估、应对与问题跟踪闭环，系统化管控项目风险。' },
      { title: '实时数据同步', description: '全流程数据实时更新，保障项目经理与各部门基于同一数据决策。' }
    ]
  },
  {
    title: '智能数据分析',
    subtitle: '战略决策的“价值透视镜”',
    previewAlt: '智能数据分析示意图',
    previewImage: projectSectionDiagram5,
    items: [
      { title: '多维度实时分析', description: '支持按项目、部门、客户、区域等维度分析进度、成本与利润率。' },
      { title: '沉淀复用经验', description: '将项目模板、WBS标准、经验教训固化，支持新项目快速复制落地。' },
      { title: '战略目标分解', description: '将战略目标拆解为具体项目，以项目数据支撑落地与动态调整。' },
      { title: '全维度报表支撑', description: '提供预算承诺成本对比、可用预算、进度分析等多类管理报表。' }
    ]
  },
  {
    title: '差异化价值',
    subtitle: '项目驱动型企业的“专属管理方案”',
    previewAlt: '差异化价值示意图',
    previewImage: projectSectionDiagram6,
    items: [
      { title: '精准匹配项目驱动型企业', description: '适配合同金额高、周期长、以项目为核心业务的复杂协作模式。' },
      { title: '覆盖全规模企业需求', description: '支持从初创到大型企业按需适配，满足不同行业与规模管理诉求。' },
      { title: '非孤立的集成化能力', description: '与ERP核心模块原生深度集成，实现项目成本与真实成本一致。' },
      { title: '全生命周期价值管控', description: '贯穿立项到结算，构建价值闭环，支撑项目利润最大化。' }
    ]
  }
];

function FeatureSection({ section }: { section: FeatureSectionData }) {
  return (
    <section className="section">
      <div className="container">
        <h2 className="section-title">{section.title}</h2>
        <p className="section-subtitle feature-subtitle">{section.subtitle}</p>
        <div className="feature-split">
          <div className="feature-list">
            {section.items.map((item, index) => (
              <article key={`${section.title}-${item.title}`} className="feature-item feature-item-static">
                <div className="feature-head">
                  <span className="feature-title">{item.title}</span>
                  <span className="feature-index">#{index + 1}</span>
                </div>
                <p className="feature-desc">{item.description}</p>
              </article>
            ))}
          </div>
          <div className="feature-preview">
            <div className="feature-placeholder">
              <img className="feature-preview-image" src={section.previewImage} alt={section.previewAlt} loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Project() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    const note =
      '项目管理模块以全生命周期闭环管控为核心，融合业财一体化与跨部门协同，解决流程不标准、协同低效、进度失控、决策缺支撑等痛点，实现从立项到结算的全链路标准化管理与价值增长。';

    const noteTips: NoteTip[] = [
      { value: '+70%', label: '成本管控精准度' },
      { value: '+60%', label: '跨部门协同效率' },
      { value: '+90%', label: '项目风险预警响应速度' }
    ];

    return {
      src: bannerBg,
      mobileSrc: bannerBg,
      title: '十大应用-项目',
      subheading: '',
      legend: '驱动项目全生命周期的价值管控引擎',
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, []);

  return (
    <div className="project-page">
      <div className="aip-banner-wrap project-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      <main>
        {featureSections.map((section) => (
          <FeatureSection key={section.title} section={section} />
        ))}

        {/* 行业实践案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">行业实践案例</h2>
            <p className="section-subtitle feature-subtitle">智慧零售运营中枢实践案例</p>
            <div className="cases">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">智慧零售全渠道业务增长实践</h3>
                    <p className="case-desc">
                      通过一盘货、统一订单中心和客户引擎协同联动，帮助企业提升库存周转效率、履约人效与会员复购表现。
                    </p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src={case01} alt="零售案例配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}
